import { shell } from '../app';
import { NetworkError } from '../data/api';
import { loadRoute, saveRoute, unwatched } from '../data/corridor';
import type { StoredRoute } from '../data/corridor';
import type { LatLng } from '../logic/route';
import { currentPath, navigate } from '../ui/router';

/** Shared by the follow-up (D50) and new-business (D57) route buttons. */

/** Start from the phone, or from the first place on the route when location is off. */
export const routeAnchor = (here: LatLng | null, first: { place_id: string }): StoredRoute['anchor'] =>
  here
    ? { kind: 'device_position', lat: here.lat, lng: here.lng, captured_at: new Date().toISOString() }
    : { kind: 'stop_derived', stop_id: first.place_id };

/** Save a freshly planned day route and open the Route tab — unless the rep moved to another screen
 *  while it was planning — with undo when it replaced a route that had stops. */
export const commitPlannedRoute = (route: StoredRoute, leftOut: number, origin: string): void => {
  const previous = loadRoute();
  unwatched(saveRoute(route));
  if (currentPath() === origin) navigate('#/route');
  const planned = `${route.stops.length} stop${route.stops.length === 1 ? '' : 's'} planned${
    leftOut ? ` · ${leftOut} without an address left out` : ''
  }`;
  if (previous?.stops.length) {
    shell.toast(`${planned} — tap to undo`, {
      duration: 8000,
      onClick: () => {
        unwatched(saveRoute(previous));
        if (currentPath() === '/route') navigate('#/route', { replace: true }); // re-render it
      },
    });
  } else {
    shell.toast(planned, { kind: 'success' });
  }
};

/** The connection is blamed only when the failure was one (a request that never got an answer, the
 *  Maps script not loading): navigator.onLine is not trusted (D68), and a Maps key or quota error
 *  must show its own message. */
export const planErrorMessage = (err: unknown): string => {
  const message = err instanceof Error ? err.message : String(err);
  return err instanceof NetworkError || /could not load|failed to fetch|network/i.test(message)
    ? 'New addresses need a connection to plan the route.'
    : `Could not plan the route: ${message}`;
};
